import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  TrendingUp, Filter, Download, Users, RefreshCw, ChevronLeft, ChevronRight,
} from "lucide-react";
import Layout from "../../components/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import { leadsAPI, vendedoresAPI } from "../../services/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const statusLabel = (status, email_confirmado) => {
  if (status === 0) return { text: "Suspenso", color: "bg-gray-500/20 text-gray-500" };
  if (!email_confirmado) return { text: "Pendente", color: "bg-yellow-500/20 text-yellow-600" };
  return { text: "Ativo", color: "bg-green-500/20 text-green-500" };
};

const toCsv = (rows) => {
  if (!rows?.length) return "";
  const headers = [
    "data", "nome", "email", "telefone", "status",
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gclid", "fbclid", "vendedor_codigo", "vendedor_nome", "plano",
  ];
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map(r => [
    formatDate(r.create_at),
    r.nome, r.email, r.telefone,
    statusLabel(r.status, r.email_confirmado).text,
    r.utm_source || "", r.utm_medium || "", r.utm_campaign || "",
    r.utm_term || "", r.utm_content || "",
    r.gclid || "", r.fbclid || "",
    r.vendedor_codigo || "", r.vendedor_nome || "",
    r.plano_nome || "",
  ].map(escape).join(","));
  return [headers.join(","), ...lines].join("\n");
};

const AdminLeads = () => {
  const { darkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    utm_source: "",
    utm_campaign: "",
    vendedor_id: "",
    desde: "",
    ate: "",
    status: "",
    search: "",
  });

  useEffect(() => {
    // Carregar vendedores (para filtro)
    vendedoresAPI.list().then(r => setVendedores(r.data || [])).catch(() => {});
  }, []);

  const queryParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  useEffect(() => {
    load();
  }, [queryParams, page]);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, resumoRes] = await Promise.all([
        leadsAPI.list({ ...queryParams, page, limit }),
        leadsAPI.resumo(queryParams),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setResumo(resumoRes.data);
    } catch (err) {
      toast.error("Erro ao carregar leads");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csv = toCsv(rows);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-nomoz-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const cardClass = darkMode ? "bg-zinc-800 border-zinc-700" : "bg-white border-gray-200";
  const inputClass = darkMode
    ? "bg-zinc-700 border-gray-600 text-white"
    : "bg-white border-gray-300 text-gray-900";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <TrendingUp className={darkMode ? "text-gray-200" : "text-gray-700"} />
            <h1 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
              Leads
            </h1>
            {resumo && (
              <span className={`ml-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                {resumo.total} leads · {resumo.ativos} ativos
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <RefreshCw size={14} /> Atualizar
            </button>
            <button
              onClick={handleExport}
              disabled={!rows.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-nomoz-orange text-white hover:opacity-90 disabled:opacity-50"
            >
              <Download size={14} /> CSV
            </button>
          </div>
        </div>

        {/* Resumo por fonte */}
        {resumo && resumo.por_source?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg border ${cardClass}`}>
              <h3 className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Por origem
              </h3>
              <ul className="mt-2 space-y-1">
                {resumo.por_source.slice(0, 5).map((s) => (
                  <li key={s.utm_source} className="flex items-center justify-between text-sm">
                    <span className={darkMode ? "text-gray-200" : "text-gray-800"}>{s.utm_source}</span>
                    <span className={darkMode ? "text-gray-400" : "text-gray-600"}>
                      {s.total} <small className="opacity-70">({s.ativos} ativos)</small>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-4 rounded-lg border ${cardClass}`}>
              <h3 className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Top campanhas
              </h3>
              <ul className="mt-2 space-y-1">
                {resumo.por_campaign.slice(0, 5).map((c, i) => (
                  <li key={c.utm_campaign + i} className="flex items-center justify-between text-sm">
                    <span className={`truncate ${darkMode ? "text-gray-200" : "text-gray-800"}`} title={c.utm_campaign}>
                      {c.utm_campaign}
                    </span>
                    <span className={darkMode ? "text-gray-400" : "text-gray-600"}>{c.total}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-4 rounded-lg border ${cardClass}`}>
              <h3 className={`text-xs uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Por vendedor
              </h3>
              <ul className="mt-2 space-y-1">
                {resumo.por_vendedor?.length ? (
                  resumo.por_vendedor.slice(0, 5).map((v) => (
                    <li key={v.vendedor_id} className="flex items-center justify-between text-sm">
                      <span className={darkMode ? "text-gray-200" : "text-gray-800"}>{v.vendedor_nome}</span>
                      <span className={darkMode ? "text-gray-400" : "text-gray-600"}>{v.total}</span>
                    </li>
                  ))
                ) : (
                  <li className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Nenhum lead atribuído a vendedor
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className={`p-4 rounded-lg border mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 ${cardClass}`}>
          <div className="flex items-center gap-2 md:col-span-4">
            <Filter size={14} className={darkMode ? "text-gray-300" : "text-gray-700"} />
            <span className={`text-sm font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Filtros</span>
          </div>
          <input
            type="text"
            placeholder="Busca (nome, email, telefone)"
            value={filters.search}
            onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          />
          <input
            type="text"
            placeholder="utm_source (ex: google)"
            value={filters.utm_source}
            onChange={(e) => { setPage(1); setFilters({ ...filters, utm_source: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          />
          <input
            type="text"
            placeholder="utm_campaign"
            value={filters.utm_campaign}
            onChange={(e) => { setPage(1); setFilters({ ...filters, utm_campaign: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          />
          <select
            value={filters.vendedor_id}
            onChange={(e) => { setPage(1); setFilters({ ...filters, vendedor_id: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          >
            <option value="">Todos os vendedores</option>
            {vendedores.map(v => (
              <option key={v.id} value={v.id}>{v.nome} ({v.codigo})</option>
            ))}
          </select>
          <input
            type="date"
            value={filters.desde}
            onChange={(e) => { setPage(1); setFilters({ ...filters, desde: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          />
          <input
            type="date"
            value={filters.ate}
            onChange={(e) => { setPage(1); setFilters({ ...filters, ate: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          />
          <select
            value={filters.status}
            onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}
            className={`px-3 py-2 rounded-md border text-sm ${inputClass}`}
          >
            <option value="">Qualquer status</option>
            <option value="ativo">Ativos</option>
            <option value="suspenso">Suspensos</option>
          </select>
          <button
            onClick={() => { setPage(1); setFilters({ utm_source: "", utm_campaign: "", vendedor_id: "", desde: "", ate: "", status: "", search: "" }); }}
            className={`px-3 py-2 rounded-md text-sm ${darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            Limpar filtros
          </button>
        </div>

        {/* Tabela */}
        <div className={`rounded-lg overflow-hidden border ${cardClass}`}>
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={40} className="mx-auto mb-3 text-gray-400" />
              <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                Nenhum lead encontrado para esses filtros.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={darkMode ? "bg-zinc-700 text-gray-200" : "bg-gray-50 text-gray-700"}>
                  <tr>
                    <th className="text-left px-4 py-3">Data</th>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Origem</th>
                    <th className="text-left px-4 py-3">Campanha</th>
                    <th className="text-left px-4 py-3">Vendedor</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className={darkMode ? "text-gray-200" : "text-gray-800"}>
                  {rows.map((r) => {
                    const st = statusLabel(r.status, r.email_confirmado);
                    return (
                      <tr key={r.id} className={darkMode ? "border-t border-zinc-700" : "border-t border-gray-100"}>
                        <td className="px-4 py-3 text-xs opacity-80 whitespace-nowrap">{formatDate(r.create_at)}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.nome}</div>
                          <div className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{r.email}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {r.utm_source ? (
                            <span>
                              <b>{r.utm_source}</b>
                              {r.utm_medium && <span className="opacity-70"> / {r.utm_medium}</span>}
                            </span>
                          ) : (
                            <span className="opacity-50">direct</span>
                          )}
                          {r.gclid && <div className="text-[10px] opacity-60">gclid ✓</div>}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {r.utm_campaign || <span className="opacity-50">—</span>}
                          {r.utm_term && <div className="text-[10px] opacity-60">“{r.utm_term}”</div>}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {r.vendedor_nome ? (
                            <span>
                              {r.vendedor_nome}
                              <div className="text-[10px] font-mono opacity-60">{r.vendedor_codigo}</div>
                            </span>
                          ) : (
                            <span className="opacity-50">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${st.color}`}>{st.text}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginação */}
        {total > limit && (
          <div className="flex items-center justify-end gap-3 mt-4">
            <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              Página {page} de {totalPages} · {total} leads
            </span>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`p-2 rounded-md disabled:opacity-30 ${darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`p-2 rounded-md disabled:opacity-30 ${darkMode ? "bg-zinc-700 text-gray-200 hover:bg-zinc-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminLeads;
